<script setup lang="ts">
import { onMounted, ref } from 'vue';
import type { PublicNotice } from '../api/notices.js';
import { usePlatformNotices } from '../stores/platformNotices.js';
import { fmtRange, fmtShort } from '../ui/format.js';
import Icon from './Icon.vue';

/**
 * 平台公告横幅(维护 / 政策更新):挂在各端外壳顶栏下面,和机构只读横幅同一位置;
 * 维护预告可关,维护进行中的不能关;政策更新点开看全文。
 */
const store = usePlatformNotices();
onMounted(() => store.start());
const open = ref<string | null>(null);

function text(n: PublicNotice): string {
  if (n.category === 'maintenance') {
    if (store.inWindow(n))
      return `系统维护中,预计 ${fmtShort(n.windowEnd).slice(6)} 恢复;进行中的考试不受影响。`;
    return `系统维护预告:${fmtRange(n.windowStart, n.windowEnd)} 暂停服务,进行中的考试不受影响。`;
  }
  return `平台公告:${n.title}`;
}
</script>

<template>
  <div
    v-for="n in store.banners"
    :key="n.id"
    class="pnb"
    :class="{ live: store.inWindow(n), policy: n.category === 'policy' }"
    role="status"
  >
    <div class="in">
      <Icon :name="n.category === 'maintenance' ? 'clock' : 'bell'" :size="16" />
      <span class="t">{{ text(n) }}</span>
      <button
        v-if="n.category === 'policy' || n.content"
        class="lk"
        @click="open = open === n.id ? null : n.id"
      >
        {{ open === n.id ? '收起' : '详情' }}
      </button>
      <button v-if="!store.inWindow(n)" class="x" aria-label="不再显示这条" @click="store.dismiss(n.id)">
        <Icon name="x" :size="14" />
      </button>
    </div>
    <div v-if="open === n.id" class="in more">{{ n.content }}</div>
  </div>
</template>

<style scoped>
.pnb {
  background: var(--brand-050);
  border-bottom: 1px solid var(--brand-150);
  color: var(--brand-700, var(--brand-600));
  font-size: 13.5px;
}
.pnb.live {
  background: var(--warn-100);
  border-color: var(--amber-line);
  color: var(--warn-ink);
}
.in {
  max-width: 1180px;
  margin: 0 auto;
  min-height: 40px;
  padding: 8px 0;
  display: flex;
  align-items: center;
  gap: 8px;
}
.t {
  flex: 1;
  min-width: 0;
}
.more {
  min-height: 0;
  padding: 0 0 10px 24px;
  color: var(--ink-2);
  white-space: pre-wrap;
}
.lk,
.x {
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 6px;
}
.x {
  display: grid;
  place-items: center;
  font-weight: 400;
}
.lk:hover,
.x:hover {
  background: rgba(255, 255, 255, 0.6);
}
.lk:focus-visible,
.x:focus-visible {
  outline: 2px solid var(--brand);
  outline-offset: 1px;
}
/* ── 学生端 H5 ── */
.h5 .pnb {
  font-size: var(--m-fs-sm);
}
.h5 .in {
  padding: 8px var(--m-gutter);
  align-items: flex-start;
}
.h5 .more {
  padding: 0 var(--m-gutter) 10px;
}
.h5 .lk,
.h5 .x {
  min-width: 32px;
  min-height: 28px;
}
</style>
